import { z } from 'zod/v4';

export type PaymentScheduleExplainer = string | object | object[] | null;

const percentageDueSchema = z.number();

const fixedDepositStageSchema = z.object({
  type: z.literal('deposit'),
  description: z.string(),
  basis: z.object({
    type: z.literal('fixed'),
    fixedAmount: z.number().int().positive(),
    percentageCap: z.number().gt(0).max(100).nullable(),
  }),
});

export type FixedDepositStage = z.infer<typeof fixedDepositStageSchema>;

/** A stage of a stored payment schedule, as every reader sees it. */
export const storedPaymentScheduleStageSchema = z.union([
  z.object({
    type: z.literal('deposit'),
    description: z.string(),
    basis: z.object({
      type: z.literal('percentage'),
      percentageDue: percentageDueSchema,
    }),
  }),
  fixedDepositStageSchema,
  z.object({
    type: z.literal('instalment'),
    description: z.string(),
    percentageDue: percentageDueSchema,
  }),
]);

export type StoredPaymentScheduleStage = z.infer<
  typeof storedPaymentScheduleStageSchema
>;

export type StoredDepositBasis = Extract<
  StoredPaymentScheduleStage,
  { type: 'deposit' }
>['basis'];

export type StoredPaymentSchedule = {
  stages: StoredPaymentScheduleStage[];
  explainer?: PaymentScheduleExplainer;
};

/** Minimal invoice shape needed for sync checking */
export type PaymentScheduleSyncInvoice = {
  totalValue: number | null;
  isDepositInvoice: boolean | null;
  isGrantInvoice?: boolean | null;
  grantLineId?: number | null;
};

/** A grant to reconcile as its own stage against its grant invoice. */
export type PaymentScheduleGrant = {
  grantLineId: number;
  description: string;
  value: number;
};

/**
 * A payment schedule stage with its value resolved and any grants overlaid. A
 * grant stage carries a fixed amount and no percentage; a stage a grant fully
 * consumes is absent.
 */
export type EffectivePaymentScheduleStage = {
  description: string;
  percentageDue: number | null;
  isDeposit: boolean;
  isGrant: boolean;
  /** Minor units. */
  value: number;
  isPaid: boolean;
};

export type EffectivePaymentSchedule = {
  stages: EffectivePaymentScheduleStage[];
  explainer: PaymentScheduleExplainer;
  /** Whether the stages still agree with the invoices raised against them. */
  isInSync: boolean;
};
